/**
 * Announcement bar countdown timer.
 *
 * Renders a live countdown toward a deadline supplied as a Unix timestamp
 * (seconds) via the `data-deadline` attribute. Timezone-safe: the deadline is
 * an absolute epoch value produced server-side from the metaobject's
 * `countdown_date` field, so no client-side timezone math is required.
 *
 * Expected markup (see sections/header-announcements.liquid):
 *   <announcement-countdown data-deadline="1757314740">
 *     <span class="announcement-countdown__label">SALE ENDS</span>
 *     <span class="announcement-countdown__time">
 *       <span class="announcement-countdown__unit"><span data-days>00</span>d</span>
 *       <span class="announcement-countdown__unit"><span data-hours>00</span>h</span>
 *       <span class="announcement-countdown__unit"><span data-minutes>00</span>m</span>
 *       <span class="announcement-countdown__unit"><span data-seconds>00</span>s</span>
 *     </span>
 *   </announcement-countdown>
 *
 * Self-contained: no module dependencies, safe to load with a plain deferred
 * <script>. Multiple instances on a page are supported.
 */
class AnnouncementCountdown extends HTMLElement {
  connectedCallback() {
    const seconds = parseInt(this.getAttribute('data-deadline'), 10);
    if (!seconds || Number.isNaN(seconds)) return;

    this.deadline = seconds * 1000;
    this.daysEl = this.querySelector('[data-days]');
    this.hoursEl = this.querySelector('[data-hours]');
    this.minutesEl = this.querySelector('[data-minutes]');
    this.secondsEl = this.querySelector('[data-seconds]');

    this.update();
    this.timer = window.setInterval(() => this.update(), 1000);
  }

  disconnectedCallback() {
    this.stop();
  }

  stop() {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = undefined;
    }
  }

  update() {
    const diff = this.deadline - Date.now();

    if (diff <= 0) {
      this.setDigits(0, 0, 0, 0);
      this.stop();
      // Countdown has expired: drop just the timer line, leaving the headline.
      const line = this.closest('.announcement-bar__promo-countdown');
      if (line) {
        line.remove();
      } else {
        this.remove();
      }
      return;
    }

    const totalSeconds = Math.floor(diff / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;

    this.setDigits(days, hours, minutes, secs);
  }

  setDigits(days, hours, minutes, secs) {
    const pad = (value) => String(value).padStart(2, '0');
    if (this.daysEl) this.daysEl.textContent = pad(days);
    if (this.hoursEl) this.hoursEl.textContent = pad(hours);
    if (this.minutesEl) this.minutesEl.textContent = pad(minutes);
    if (this.secondsEl) this.secondsEl.textContent = pad(secs);
  }
}

if (!customElements.get('announcement-countdown')) {
  customElements.define('announcement-countdown', AnnouncementCountdown);
}
